import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

interface PlatformInfo {
  apex: string | null;
  edgeTarget: string | null;
  ip: string | null;
}

interface Props {
  orgSlug: string;
  projectSlug: string;
  rowId: string;
  hostname: string;
  kind: "apex" | "subdomain";
}

/**
 * Step 2 — show DNS instructions. Subdomain → CNAME → edgeTarget;
 * apex → A record → platform IP. Verify button submits POST /verify.
 */
export function DnsInstructions({ orgSlug, projectSlug, rowId, hostname, kind }: Props) {
  const qc = useQueryClient();
  const platform = useQuery({
    queryKey: ["platform"],
    queryFn: () => api.get<PlatformInfo>("/platform"),
    staleTime: 60_000,
  });

  const verify = useMutation({
    mutationFn: () =>
      api.post(`/orgs/${orgSlug}/projects/${projectSlug}/domains/${rowId}/verify`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project-domains", orgSlug, projectSlug] }),
  });

  const value =
    kind === "subdomain"
      ? platform.data?.edgeTarget ?? "—"
      : platform.data?.ip ?? "—";
  const recordType = kind === "subdomain" ? "CNAME" : "A";

  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground">
        Add this record at your DNS provider:
      </div>
      <div className="rounded-md border border-border bg-subtle px-3 py-2 space-y-1.5">
        <Field label="Type" value={recordType} />
        <Field label="Name" value={hostname} />
        <Field label="Value" value={value} copyable />
      </div>
      <div className="text-2xs text-subtle-foreground">
        Propagation usually takes 1–2 minutes. Once added, click Verify and we'll keep checking for 5 minutes.
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant="primary"
          size="sm"
          disabled={verify.isPending}
          onClick={() => verify.mutate()}
        >
          {verify.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Verify
        </Button>
      </div>
    </div>
  );
}

function Field({ label, value, copyable }: { label: string; value: string; copyable?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className="w-14 text-subtle-foreground">{label}</span>
      <span className="flex-1 font-mono truncate">{value}</span>
      {copyable && (
        <Button
          size="icon"
          variant="ghost"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
            } catch {
              /* clipboard may be blocked over plain http */
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          }}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      )}
    </div>
  );
}
